/**
 * WaitlistTab — operator view of the booking waitlist. Anyone who couldn't
 * grab a full slot lands here. When a matching booking cancels, the top
 * entry is emailed automatically (see backend _fire_waitlist_autofill).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trash2, Users, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const map = {
    waiting: 'bg-amber-100 text-amber-800',
    offered: 'bg-sky-100 text-sky-800',
    fulfilled: 'bg-emerald-100 text-emerald-800',
    expired: 'bg-slate-100 text-slate-600',
  };
  return <Badge className={`${map[status] || 'bg-slate-100'} border-0`}>{status}</Badge>;
};

export default function WaitlistTab() {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('waiting');
  const [services, setServices] = useState([]);
  const [staleBackend, setStaleBackend] = useState(false);

  const load = async () => {
    try {
      const [w, s] = await Promise.all([
        axios.get(`${API}/api/booking/waitlist?status=${filter}`, auth),
        axios.get(`${API}/api/booking/services`, auth),
      ]);
      setItems(w.data || []);
      setServices(s.data || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 405) {
        setStaleBackend(true);
        setItems([]);
      } else {
        toast.error('Failed to load waitlist: ' + (e?.response?.data?.detail || e.message));
      }
    }
  };

  useEffect(() => { load(); }, [filter]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/booking/waitlist/${id}`, auth);
      toast.success('Removed from waitlist');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  const svcName = (sid) => services.find((s) => s.id === sid)?.name || sid;

  return (
    <div className="space-y-4" data-testid="waitlist-tab">
      {staleBackend && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="waitlist-stale-backend">
          <div className="font-semibold text-amber-900 mb-1">Waitlist endpoint not deployed on this backend yet</div>
          <p className="text-amber-800">
            The Waitlist feature is included in the frontend but this server&apos;s backend still runs an older version. Re-run the Plesk deploy (or manually rsync backend/ → /backend/ and restart <code>konnect-backend</code>).
          </p>
        </div>
      )}
      <div className="flex items-center gap-2">
        {['waiting', 'offered', 'fulfilled', 'expired', 'all'].map((s) => (
          <Button
            key={s}
            variant={filter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(s)}
            data-testid={`waitlist-filter-${s}`}
          >
            {s}
          </Button>
        ))}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No one on the {filter} waitlist yet.</p>
            <p className="text-xs text-slate-400 mt-1">Clients join automatically when they hit a full slot on your public booking page.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((w) => (
            <Card key={w.id} data-testid={`waitlist-row-${w.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{w.contact_name || 'Guest'}</span>
                      <StatusBadge status={w.status} />
                      {w.offered_at && (
                        <span className="text-[11px] text-sky-700 inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> offered {new Date(w.offered_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {w.email} {w.phone && `· ${w.phone}`}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Wants: <strong>{svcName(w.service_id)}</strong>{w.preferred_start_at ? ` around ${new Date(w.preferred_start_at).toLocaleString()}` : ''}
                    </div>
                    {w.notes && <div className="text-xs text-slate-500 mt-1 italic">&ldquo;{w.notes}&rdquo;</div>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(w.id)} data-testid={`waitlist-remove-${w.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
